
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Skeleton,
    TextField,
    Button,
} from "@mui/material";
import ReactHtmlParser from "html-react-parser";
import { formatDate } from "utils/dateFormats";
import FormDrawer from "components/Shared/FormDrawer";
import StagePathway from "components/Shared/StagePathway";
import { toast } from "react-toastify";
import { useAuth } from "hooks";
import {
    useUpdateShipmentActionMutation,
    useGetShipmentActionResponsesQuery
} from "store/services/shipmentActionApi";

const ShipmentActionDetailDrawer = ({ open, onClose, action, refetchActions }) => {
    const [responseText, setResponseText] = useState("");
    const [activeStatus, setActiveStatus] = useState("Read");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { user } = useAuth();
    const [updateShipmentAction] = useUpdateShipmentActionMutation();
    const {
        data: responses,
        isLoading: isResponsesLoading,
        refetch: refetchResponses
    } = useGetShipmentActionResponsesQuery(
        action?._id,
        { skip: !open || !action?._id }
    );

    useEffect(() => {
        if (open && action?._id) {
            setActiveStatus(action.status === "Closed" ? "Closed" : "Read");
            if (action.status === "Open") {
                updateShipmentAction({ id: action._id, status: "Read" })
                    .unwrap()
                    .then(() => refetchActions())
                    .catch(() => { });
            }
        }
    }, [open, action, updateShipmentAction, refetchActions]);

    const handleAddResponse = async (closeAfter = false) => {
        try {
            setIsSubmitting(true);
            await updateShipmentAction({
                id: action._id,
                response: responseText,
                status: closeAfter ? "Closed" : "Read",
            }).unwrap();
            toast.info(closeAfter ? "Response submitted and closed" : "Response added");
            setActiveStatus(closeAfter ? "Closed" : "Read");
            setResponseText("");
            // Force refetch responses and actions
            await refetchResponses();
            await refetchActions();
            if (closeAfter) {
                // Small delay to ensure state updates before closing
                setTimeout(() => onClose(), 100);
            }
        } catch (err) {
            toast.error(err?.data?.message ?? "Failed to submit response");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Reset form when drawer closes
    useEffect(() => {
        if (!open) {
            setResponseText("");
            setIsSubmitting(false);
        }
    }, [open]);

    const statusStages = ["Open", "Read", "Closed"];

    return (
        <FormDrawer title="Shipment Action Detail" open={open} onClose={onClose}>
            <Box sx={{ display: "grid", gap: 2, p: 2 }}>
                {!action ? (
                    Array(5)
                        .fill(null)
                        .map((_, i) => <Skeleton key={i} height={60} />)
                ) : (
                    <>
                        <Box sx={{ pointerEvents: "none" }}>
                            <StagePathway
                                stages={statusStages}
                                activeStage={activeStatus}
                                getStageLabel={(s) => s}
                                getStageValue={(s) => s}
                                onChange={() => { }}
                            />
                        </Box>



                        <Typography variant="subtitle2">Type: {action?.systemType || "N/A"}</Typography>
                        <Typography variant="subtitle2">Action Type: {action?.actionType || "N/A"}</Typography>
                        <Typography variant="subtitle2">Responsible: {action?.responsible?.name || "N/A"}</Typography>
                        <Typography variant="subtitle2">Vendor: {action?.vendor?.shopName || "N/A"}</Typography>
                        <Typography variant="subtitle2">Created At: {formatDate(action?.createdAt)}</Typography>

                        {action?.notification && (
                            <Box>
                                <Typography variant="subtitle2">Notification:</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>{action.notification.title}</strong>: {action.notification.message} ({formatDate(action.notification.createdAt)})
                                </Typography>
                            </Box>
                        )}

                        <Box>
                            <Typography variant="subtitle2">Who Can Close:</Typography>
                            <Typography variant="body2" color="text.secondary">{action?.whoCanClose || "N/A"}</Typography>
                        </Box>
                        <Typography variant="subtitle2">Created User: {action?.createdBy?.name || "N/A"}</Typography>

                        {action?.description ? (
                            <Box>
                                <Typography variant="subtitle2">Description:</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {ReactHtmlParser(action.description)}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography variant="subtitle2" color="text.secondary">
                                No description provided.
                            </Typography>

                        )}
                        <Typography variant="subtitle2" sx={{ color: "green" }}>
                            Status: {activeStatus}
                        </Typography>

                        <Box>
                            <Typography variant="subtitle2">Add New Response:</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                value={responseText}
                                onChange={(e) => setResponseText(e.target.value)}
                                variant="outlined"
                                placeholder="Enter response here"
                                disabled={isSubmitting}
                            />
                        </Box>

                        <Box>
                            <Typography variant="subtitle2">Response History:</Typography>
                            {isResponsesLoading ? (
                                Array(3).fill(null).map((_, i) => <Skeleton key={i} height={60} />)
                            ) : responses?.length > 0 ? (
                                responses.map((response, index) => (
                                    <Box key={index} sx={{ mb: 2, p: 2, border: "1px solid #ddd", borderRadius: 2 }}>
                                        <Typography variant="body2">
                                            <strong>
                                                {response.responderType === "VendorProfile" &&
                                                    response.responder?._id?.toString() === user?._id?.toString()
                                                    ? "You"
                                                    : "Admin"}
                                            </strong>{" "}
                                            - {formatDate(response.createdAt)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {ReactHtmlParser(response.text)}
                                        </Typography>
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No responses yet.
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                            <Button
                                variant="contained"
                                onClick={() => handleAddResponse(false)}
                                disabled={!responseText.trim() || isSubmitting}
                            >
                                {isSubmitting ? "Submitting..." : "Submit Response"}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => handleAddResponse(true)}
                                disabled={!responseText.trim() || isSubmitting}
                            >
                                {isSubmitting ? "Submitting..." : "Submit and Close"}
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </FormDrawer>
    );
};

export default ShipmentActionDetailDrawer;